/** Small DOM helper. Text is always set as text, never as HTML. */
type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, unknown> & { class?: string };

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs | null = null, ...children: (Child | Child[])[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === "class") el.className = String(v);
    else if (k === "value" && "value" in el) (el as HTMLInputElement).value = String(v);
    else if (k === "checked" || k === "disabled" || k === "selected") (el as unknown as Record<string, boolean>)[k] = Boolean(v);
    else el.setAttribute(k, v === true ? "" : String(v));
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : String(c));
  }
  return el;
}

export function mount(target: HTMLElement, ...nodes: (Child | Child[])[]): void {
  target.replaceChildren(...nodes.flat().filter((n): n is Node | string | number => n !== null && n !== undefined && n !== false).map((n) => (n instanceof Node ? n : String(n))));
}

export const gbp = (pence: number | null | undefined): string => {
  if (pence === null || pence === undefined) return "–";
  const abs = Math.abs(pence);
  return `${pence < 0 ? "-" : ""}£${(abs / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const num = (n: number | null | undefined, dp = 0): string =>
  n === null || n === undefined ? "–" : n.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export const date = (iso: string | null | undefined): string => {
  if (!iso) return "–";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" });
};

export const pct = (part: number, whole: number): string => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–");

export function stat(label: string, value: string, note?: string, tone?: "good" | "warn" | "bad"): HTMLElement {
  return h("div", { class: `stat${tone ? ` stat-${tone}` : ""}` }, h("div", { class: "stat-value" }, value), h("div", { class: "stat-label" }, label), note ? h("div", { class: "stat-note" }, note) : null);
}

export function notice(kind: "info" | "warn" | "error" | "ok", ...children: Child[]): HTMLElement {
  return h("div", { class: `notice notice-${kind}`, role: kind === "error" ? "alert" : "status" }, ...children);
}

/** Run an async action from a button, showing progress and any error next to it. */
export function action(label: string, run: (btn: HTMLButtonElement, status: HTMLElement) => Promise<void>, opts: { primary?: boolean; disabled?: boolean } = {}): HTMLElement {
  const status = h("span", { class: "action-status", "aria-live": "polite" });
  const btn = h("button", { type: "button", class: opts.primary ? "btn btn-primary" : "btn", disabled: opts.disabled }, label);
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    status.textContent = "Working…";
    status.className = "action-status";
    status.removeAttribute("role");
    try {
      await run(btn, status);
      if (status.textContent === "Working…") status.textContent = "";
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : String(e);
      status.className = "action-status action-error";
      // Errors are announced straight away to screen readers.
      status.setAttribute("role", "alert");
    } finally {
      btn.disabled = false;
    }
  });
  return h("span", { class: "action" }, btn, status);
}

export function table(headers: string[], rows: Child[][], opts: { numeric?: number[]; caption?: string } = {}): HTMLElement {
  const isNum = new Set(opts.numeric ?? []);
  return h("div", { class: "table-wrap stackable" },
    h("table", null,
      opts.caption ? h("caption", null, opts.caption) : null,
      h("thead", null, h("tr", null, headers.map((t, i) => h("th", { class: isNum.has(i) ? "num" : undefined, scope: "col" }, t || h("span", { class: "sr-only" }, "Actions"))))),
      h("tbody", null, rows.map((r) => h("tr", null, r.map((c, i) => h("td", { class: isNum.has(i) ? "num" : undefined, "data-label": headers[i] }, c))))),
    ),
  );
}

/**
 * A small "Delete" button for one row (a note, a touch, a report). Asks first, names what it
 * deletes for screen readers, and shows any error next to it. Every delete is kept in the activity log.
 */
export function deleteButton(what: string, question: string, run: () => Promise<void>): HTMLElement {
  const el = action("Delete", async () => {
    if (!confirm(question)) return;
    await run();
  });
  const btn = el.querySelector("button")!;
  btn.className = "btn btn-quiet btn-delete";
  btn.setAttribute("aria-label", `Delete ${what}`);
  btn.title = `Delete ${what}`;
  return el;
}

/** Saves text as a file (CSV by default, with a byte-order mark so Excel reads the £ sign). */
export function download(name: string, text: string, type = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob(["\ufeff", text], { type }));
  const a = h("a", { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** True when a file matches an input's accept list (".csv,.xlsx,text/csv"); an empty list accepts anything. */
export function accepts(accept: string, file: { name: string; type: string }): boolean {
  const rules = accept.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (!rules.length) return true;
  const name = file.name.toLowerCase(), type = (file.type || "").toLowerCase();
  return rules.some((r) => (r.startsWith(".") ? name.endsWith(r) : r.endsWith("/*") ? type.startsWith(r.slice(0, -1)) : type === r));
}

/**
 * Lets files be dragged onto `zone` as well as chosen with `input`. Dropped files go into the input and
 * fire its usual change event, so every upload keeps one code path. Files the input would not accept
 * are refused with a message next to the zone.
 */
export function makeDroppable(zone: HTMLElement, input: HTMLInputElement): void {
  const msg = h("span", { class: "file-drop-msg", role: "status" });
  zone.append(msg);
  zone.classList.add("drop-target");
  let depth = 0;
  zone.addEventListener("dragenter", (e) => { e.preventDefault(); depth++; zone.classList.add("is-over"); });
  zone.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; });
  zone.addEventListener("dragleave", () => { if (--depth <= 0) { depth = 0; zone.classList.remove("is-over"); } });
  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    depth = 0;
    zone.classList.remove("is-over");
    const dropped = [...(e.dataTransfer?.files ?? [])];
    const ok = dropped.filter((f) => accepts(input.accept, f)).slice(0, input.multiple ? undefined : 1);
    if (!ok.length) {
      msg.textContent = dropped.length ? `That file type is not accepted here (${input.accept.replace(/,/g, ", ")}).` : "";
      return;
    }
    msg.textContent = dropped.length > ok.length ? `${dropped.length - ok.length} file${dropped.length - ok.length === 1 ? "" : "s"} skipped: not an accepted type.` : "";
    const dt = new DataTransfer();
    ok.forEach((f) => dt.items.add(f));
    input.files = dt.files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

/** A file input inside a drop zone: drag files onto it, or click to choose. */
export function dropZone(input: HTMLInputElement, text = input.multiple ? "Drop files here, or choose" : "Drop a file here, or choose"): HTMLElement {
  const zone = h("label", { class: "file-drop file-drop-sm" }, h("span", { class: "file-drop-text" }, text), input);
  makeDroppable(zone, input);
  return zone;
}
